'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DownPaymentCalculator() {
  const t = useTranslations("tools.downPaymentCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator 1: Calculate Affordable Home Price
  const [calc1Inputs, setCalc1Inputs] = useState({
    cash: 100000,
    downPct: 20,
    includeClosing: true,
    closingVal: 3,
    closingUnit: 'percent',
    interest: 6.286,
    term: 30
  });
  const [calc1Results, setCalc1Results] = useState<any>(null);

  // Calculator 2: Calculate Required Cash
  const [calc2Inputs, setCalc2Inputs] = useState({
    homePrice: 500000,
    downPct: 20,
    includeClosing: true,
    closingVal: 3,
    closingUnit: 'percent',
    interest: 6.286,
    term: 30
  });
  const [calc2Results, setCalc2Results] = useState<any>(null);

  // Calculator 3: Calculate Down Payment Percentage
  const [calc3Inputs, setCalc3Inputs] = useState({
    homePrice: 500000,
    cash: 100000,
    includeClosing: true,
    closingVal: 3,
    closingUnit: 'percent',
    interest: 6.286,
    term: 30
  });
  const [calc3Results, setCalc3Results] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatCurrency = (value: number) => {
    return '$' + Math.round(value).toLocaleString('en-US');
  };

  const formatPercent = (value: number) => {
    return value.toFixed(1) + '%';
  };

  const calculateMonthlyPayment = (principal: number, annualRate: number, years: number) => {
    const monthlyRate = annualRate / 100 / 12;
    const numPayments = years * 12;

    if (monthlyRate === 0) {
      return principal / numPayments;
    }

    return principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
           (Math.pow(1 + monthlyRate, numPayments) - 1);
  };

  const handleCalc1Submit = (e: React.FormEvent) => {
    e.preventDefault();

    const { cash, downPct, includeClosing, closingVal, closingUnit, interest, term } = calc1Inputs;
    let homePrice, downPayment, closingCosts, loanAmount;

    if (includeClosing) {
      if (closingUnit === 'percent') {
        homePrice = cash / (downPct / 100 + closingVal / 100);
        closingCosts = homePrice * closingVal / 100;
      } else {
        homePrice = (cash - closingVal) / (downPct / 100);
        closingCosts = closingVal;
      }
    } else {
      homePrice = cash / (downPct / 100);
      closingCosts = 0;
    }

    downPayment = homePrice * downPct / 100;
    loanAmount = homePrice - downPayment;
    const monthlyPayment = calculateMonthlyPayment(loanAmount, interest, term);

    setCalc1Results({
      homePrice,
      downPayment,
      closingCosts,
      loanAmount,
      monthlyPayment,
      showPMI: downPct < 20
    });
  };

  const handleCalc2Submit = (e: React.FormEvent) => {
    e.preventDefault();

    const { homePrice, downPct, includeClosing, closingVal, closingUnit, interest, term } = calc2Inputs;

    const downPayment = homePrice * downPct / 100;
    let closingCosts = 0;

    if (includeClosing) {
      closingCosts = closingUnit === 'percent' ?
        homePrice * closingVal / 100 : closingVal;
    }

    const cashNeeded = downPayment + closingCosts;
    const loanAmount = homePrice - downPayment;
    const monthlyPayment = calculateMonthlyPayment(loanAmount, interest, term);

    setCalc2Results({
      downPayment,
      closingCosts,
      cashNeeded,
      loanAmount,
      monthlyPayment,
      showPMI: downPct < 20
    });
  };

  const handleCalc3Submit = (e: React.FormEvent) => {
    e.preventDefault();

    const { homePrice, cash, includeClosing, closingVal, closingUnit, interest, term } = calc3Inputs;

    let closingCosts = 0;
    let downPayment, downPct;

    if (includeClosing) {
      if (closingUnit === 'percent') {
        closingCosts = homePrice * closingVal / 100;
        downPayment = cash - closingCosts;
      } else {
        closingCosts = closingVal;
        downPayment = cash - closingCosts;
      }
    } else {
      downPayment = cash;
    }

    downPct = (downPayment / homePrice) * 100;
    const loanAmount = homePrice - downPayment;
    const monthlyPayment = calculateMonthlyPayment(loanAmount, interest, term);

    setCalc3Results({
      downPayment,
      downPct,
      closingCosts,
      loanAmount,
      monthlyPayment,
      showPMI: downPct < 20
    });
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    {}
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: '#2563eb', marginBottom: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const calcSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const calcSectionDescStyle = {
    color: '#64748b',
    fontSize: '0.9rem',
    marginBottom: '1.5rem'
  };

  const calcGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formGroupStyle = {
    marginBottom: '1.25rem'
  };

  const labelStyle = {
    display: 'block',
    color: '#334155',
    fontWeight: 500,
    marginBottom: '0.5rem',
    fontSize: '0.95rem'
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem' },
    { padding: '0.875rem' },
    { padding: '0.75rem' }
  );

  const inputWithUnitStyle = {
    position: 'relative' as const
  };

  const inputUnitStyle = {
    position: 'absolute' as const,
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500
  };

  const checkboxGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem'
  };

  const closingCostInputsStyle = {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center'
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: '100%',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' }
  );

  const resultsStyle = {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderRadius: '1rem',
    padding: '2rem',
    border: '2px solid #86efac'
  };

  const resultsTitleStyle = {
    color: '#15803d',
    fontSize: '1.5rem',
    marginBottom: '1.5rem',
    fontWeight: 700,
    textAlign: 'center' as const
  };

  const resultGridStyle = {
    display: 'grid',
    gap: '1rem'
  };

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    background: 'white',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #2563eb'
  };

  const resultLabelStyle = {
    color: '#475569',
    fontWeight: 500
  };

  const resultValueStyle = {
    color: '#1e293b',
    fontWeight: 700,
    fontSize: '1.1rem'
  };

  const pmiWarningStyle = {
    background: '#fef3c7',
    border: '2px solid #fbbf24',
    borderRadius: '0.5rem',
    padding: '1rem',
    marginTop: '1rem',
    color: '#92400e',
    fontSize: '0.9rem'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>{t("toolTitle")}</h2>
          <p style={summaryTextStyle}>{t("toolSubtitle")}</p>
        </div>

        <div style={calcSectionStyle}>
          <h3 style={h3Style}>{t("calc1Title")}</h3>
          <p style={calcSectionDescStyle}>{t("calc1Desc")}</p>

          <div style={calcGridStyle}>
            <div>
              <form onSubmit={handleCalc1Submit}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="cash1">{t("upfrontCash")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="cash1"
                      value={calc1Inputs.cash}
                      onChange={(e) => setCalc1Inputs({...calc1Inputs, cash: parseFloat(e.target.value) || 0})}
                      step="1000"
                      required
                    />
                    <span style={inputUnitStyle}>$</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="downpct1">{t("downPaymentPct")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="downpct1"
                      value={calc1Inputs.downPct}
                      onChange={(e) => setCalc1Inputs({...calc1Inputs, downPct: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      min="0"
                      max="100"
                      required
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <div style={checkboxGroupStyle}>
                    <input
                      type="checkbox"
                      id="closing1"
                      checked={calc1Inputs.includeClosing}
                      onChange={(e) => setCalc1Inputs({...calc1Inputs, includeClosing: e.target.checked})}
                    />
                    <label style={{...labelStyle, marginBottom: 0}} htmlFor="closing1">{t("includeClosing")}</label>
                  </div>
                  <div style={closingCostInputsStyle}>
                    <input
                      style={{...inputStyle, flex: 1}}
                      type="number"
                      id="closingval1"
                      value={calc1Inputs.closingVal}
                      onChange={(e) => setCalc1Inputs({...calc1Inputs, closingVal: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      min="0"
                    />
                    <select
                      style={{...inputStyle, width: 'auto', minWidth: '70px'}}
                      id="closingunit1"
                      value={calc1Inputs.closingUnit}
                      onChange={(e) => setCalc1Inputs({...calc1Inputs, closingUnit: e.target.value})}
                    >
                      <option value="percent">%</option>
                      <option value="dollar">$</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interest1">{t("interestRate")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="interest1"
                      value={calc1Inputs.interest}
                      onChange={(e) => setCalc1Inputs({...calc1Inputs, interest: parseFloat(e.target.value) || 0})}
                      step="0.001"
                      min="0"
                      required
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="term1">{t("loanTerm")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="term1"
                      value={calc1Inputs.term}
                      onChange={(e) => setCalc1Inputs({...calc1Inputs, term: parseFloat(e.target.value) || 0})}
                      step="1"
                      min="1"
                      required
                    />
                    <span style={inputUnitStyle}>{t("years")}</span>
                  </div>
                </div>

                <button type="submit" style={calculateBtnStyle}>{t("calculateHomePrice")}</button>
              </form>
            </div>

            <div>
              {calc1Results && (
                <div style={resultsStyle}>
                  <h4 style={resultsTitleStyle}>{t("homePrice")}: {formatCurrency(calc1Results.homePrice)}</h4>
                  <div style={resultGridStyle}>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("homePrice")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc1Results.homePrice)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("downPayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc1Results.downPayment)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("closingCosts")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc1Results.closingCosts)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("loanAmount")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc1Results.loanAmount)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("monthlyPayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc1Results.monthlyPayment)}</span>
                    </div>
                  </div>
                  {calc1Results.showPMI && (
                    <div style={pmiWarningStyle}>
                      ⚠️ {t("pmiWarning")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={calcSectionStyle}>
          <h3 style={h3Style}>{t("calc2Title")}</h3>
          <p style={calcSectionDescStyle}>{t("calc2Desc")}</p>

          <div style={calcGridStyle}>
            <div>
              <form onSubmit={handleCalc2Submit}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="homeprice2">{t("homePrice")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="homeprice2"
                      value={calc2Inputs.homePrice}
                      onChange={(e) => setCalc2Inputs({...calc2Inputs, homePrice: parseFloat(e.target.value) || 0})}
                      step="1000"
                      required
                    />
                    <span style={inputUnitStyle}>$</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="downpct2">{t("downPaymentPct")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="downpct2"
                      value={calc2Inputs.downPct}
                      onChange={(e) => setCalc2Inputs({...calc2Inputs, downPct: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      min="0"
                      max="100"
                      required
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <div style={checkboxGroupStyle}>
                    <input
                      type="checkbox"
                      id="closing2"
                      checked={calc2Inputs.includeClosing}
                      onChange={(e) => setCalc2Inputs({...calc2Inputs, includeClosing: e.target.checked})}
                    />
                    <label style={{...labelStyle, marginBottom: 0}} htmlFor="closing2">{t("includeClosing")}</label>
                  </div>
                  <div style={closingCostInputsStyle}>
                    <input
                      style={{...inputStyle, flex: 1}}
                      type="number"
                      id="closingval2"
                      value={calc2Inputs.closingVal}
                      onChange={(e) => setCalc2Inputs({...calc2Inputs, closingVal: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      min="0"
                    />
                    <select
                      style={{...inputStyle, width: 'auto', minWidth: '70px'}}
                      id="closingunit2"
                      value={calc2Inputs.closingUnit}
                      onChange={(e) => setCalc2Inputs({...calc2Inputs, closingUnit: e.target.value})}
                    >
                      <option value="percent">%</option>
                      <option value="dollar">$</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interest2">{t("interestRate")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="interest2"
                      value={calc2Inputs.interest}
                      onChange={(e) => setCalc2Inputs({...calc2Inputs, interest: parseFloat(e.target.value) || 0})}
                      step="0.001"
                      min="0"
                      required
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="term2">{t("loanTerm")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="term2"
                      value={calc2Inputs.term}
                      onChange={(e) => setCalc2Inputs({...calc2Inputs, term: parseFloat(e.target.value) || 0})}
                      step="1"
                      min="1"
                      required
                    />
                    <span style={inputUnitStyle}>{t("years")}</span>
                  </div>
                </div>

                <button type="submit" style={calculateBtnStyle}>{t("calculateCashNeeded")}</button>
              </form>
            </div>

            <div>
              {calc2Results && (
                <div style={resultsStyle}>
                  <h4 style={resultsTitleStyle}>{t("cashNeeded")}: {formatCurrency(calc2Results.cashNeeded)}</h4>
                  <div style={resultGridStyle}>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("downPayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc2Results.downPayment)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("closingCosts")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc2Results.closingCosts)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("totalCashNeeded")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc2Results.cashNeeded)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("loanAmount")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc2Results.loanAmount)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("monthlyPayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc2Results.monthlyPayment)}</span>
                    </div>
                  </div>
                  {calc2Results.showPMI && (
                    <div style={pmiWarningStyle}>
                      ⚠️ {t("pmiWarning")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={calcSectionStyle}>
          <h3 style={h3Style}>{t("calc3Title")}</h3>
          <p style={calcSectionDescStyle}>{t("calc3Desc")}</p>

          <div style={calcGridStyle}>
            <div>
              <form onSubmit={handleCalc3Submit}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="homeprice3">{t("homePrice")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="homeprice3"
                      value={calc3Inputs.homePrice}
                      onChange={(e) => setCalc3Inputs({...calc3Inputs, homePrice: parseFloat(e.target.value) || 0})}
                      step="1000"
                      required
                    />
                    <span style={inputUnitStyle}>$</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="cash3">{t("upfrontCash")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="cash3"
                      value={calc3Inputs.cash}
                      onChange={(e) => setCalc3Inputs({...calc3Inputs, cash: parseFloat(e.target.value) || 0})}
                      step="1000"
                      required
                    />
                    <span style={inputUnitStyle}>$</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <div style={checkboxGroupStyle}>
                    <input
                      type="checkbox"
                      id="closing3"
                      checked={calc3Inputs.includeClosing}
                      onChange={(e) => setCalc3Inputs({...calc3Inputs, includeClosing: e.target.checked})}
                    />
                    <label style={{...labelStyle, marginBottom: 0}} htmlFor="closing3">{t("includeClosing")}</label>
                  </div>
                  <div style={closingCostInputsStyle}>
                    <input
                      style={{...inputStyle, flex: 1}}
                      type="number"
                      id="closingval3"
                      value={calc3Inputs.closingVal}
                      onChange={(e) => setCalc3Inputs({...calc3Inputs, closingVal: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      min="0"
                    />
                    <select
                      style={{...inputStyle, width: 'auto', minWidth: '70px'}}
                      id="closingunit3"
                      value={calc3Inputs.closingUnit}
                      onChange={(e) => setCalc3Inputs({...calc3Inputs, closingUnit: e.target.value})}
                    >
                      <option value="percent">%</option>
                      <option value="dollar">$</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interest3">{t("interestRate")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="interest3"
                      value={calc3Inputs.interest}
                      onChange={(e) => setCalc3Inputs({...calc3Inputs, interest: parseFloat(e.target.value) || 0})}
                      step="0.001"
                      min="0"
                      required
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="term3">{t("loanTerm")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="term3"
                      value={calc3Inputs.term}
                      onChange={(e) => setCalc3Inputs({...calc3Inputs, term: parseFloat(e.target.value) || 0})}
                      step="1"
                      min="1"
                      required
                    />
                    <span style={inputUnitStyle}>{t("years")}</span>
                  </div>
                </div>

                <button type="submit" style={calculateBtnStyle}>{t("calculateDownPaymentPct")}</button>
              </form>
            </div>

            <div>
              {calc3Results && (
                <div style={resultsStyle}>
                  <h4 style={resultsTitleStyle}>{t("downPayment")}: {formatPercent(calc3Results.downPct)}</h4>
                  <div style={resultGridStyle}>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("downPayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc3Results.downPayment)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("downPaymentPct")}</span>
                      <span style={resultValueStyle}>{formatPercent(calc3Results.downPct)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("closingCosts")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc3Results.closingCosts)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("loanAmount")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc3Results.loanAmount)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("monthlyPayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(calc3Results.monthlyPayment)}</span>
                    </div>
                  </div>
                  {calc3Results.showPMI && (
                    <div style={pmiWarningStyle}>
                      ⚠️ {t("pmiWarning")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introPara1")}</p>
        <p style={paragraphStyle}>{t("introPara2")}</p>
        <p style={paragraphStyle}>{t("introPara3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step1Title")}</strong> {t("step1Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step2Title")}</strong> {t("step2Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step3Title")}</strong> {t("step3Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step4Title")}</strong> {t("step4Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step5Title")}</strong> {t("step5Desc")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insight1")}</p>
        <p style={paragraphStyle}>{t("insight2")}</p>
        <p style={paragraphStyle}>{t("insight3")}</p>
        <p style={paragraphStyle}>{t("insight4")}</p>
        <p style={paragraphStyle}>{t("insight5")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={h3Style}>{t("detail1Title")}</h3>
        <p style={paragraphStyle}>{t("detail1Desc")}</p>

        <h3 style={h3Style}>{t("detail2Title")}</h3>
        <p style={paragraphStyle}>{t("detail2Desc")}</p>

        <h3 style={h3Style}>{t("detail3Title")}</h3>
        <p style={paragraphStyle}>{t("detail3Desc")}</p>

        <h3 style={h3Style}>{t("detail4Title")}</h3>
        <p style={paragraphStyle}>{t("detail4Desc")}</p>

        <h3 style={h3Style}>{t("detail5Title")}</h3>
        <p style={paragraphStyle}>{t("detail5Desc")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <h3 style={h3Style}>{t("faq1Q")}</h3>
        <p style={paragraphStyle}>{t("faq1A")}</p>

        <h3 style={h3Style}>{t("faq2Q")}</h3>
        <p style={paragraphStyle}>{t("faq2A")}</p>

        <h3 style={h3Style}>{t("faq3Q")}</h3>
        <p style={paragraphStyle}>{t("faq3A")}</p>

        <h3 style={h3Style}>{t("faq4Q")}</h3>
        <p style={paragraphStyle}>{t("faq4A")}</p>

        <h3 style={h3Style}>{t("faq5Q")}</h3>
        <p style={paragraphStyle}>{t("faq5A")}</p>

        <h3 style={h3Style}>{t("faq6Q")}</h3>
        <p style={paragraphStyle}>{t("faq6A")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.hsh.com/down-payment-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref1")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.bankrate.com/mortgages/down-payment-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref2")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.rocketmortgage.com/learn/what-is-a-down-payment" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref3")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://themortgagereports.com/18520/20-percent-downpayment-risk-mortgage-interest-rate" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref4")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.nerdwallet.com/mortgages/learn/average-down-payment-on-a-house" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref5")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.fha.com/define/down-payment" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref6")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.consumerfinance.gov/about-us/blog/how-decide-how-much-spend-your-down-payment/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref7")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}